// Read-only DTOs for the Inteligencia / Vigilancia / Zonas Restringidas screen.
// Mirror MineTrace.Api.Controllers.Intelligence.RestrictedZonesIntelController.

import type { Geometry } from 'geojson'

import {
  RestrictionClass,
  type RestrictionClassValue,
  restrictionClassColor,
  restrictionClassLabel,
  sourceLabel,
} from './restrictionCheck'

export interface RestrictedZoneFeatureProperties {
  source: 'Ingemmet' | 'Sernanp' | 'CulturaSigda'
  layer: string
  name: string
  category: string | null
  restrictionClass: keyof typeof RestrictionClass
  syncedAt: string
}

export interface RestrictedZoneFeature {
  type: 'Feature'
  id: string
  geometry: Geometry
  properties: RestrictedZoneFeatureProperties
}

export interface RestrictedZoneFeatureCollection {
  type: 'FeatureCollection'
  features: RestrictedZoneFeature[]
  truncated: boolean
  limit: number
}

export interface RestrictedZoneSyncLayer {
  layer: string
  lastSyncedAt: string
  activeCount: number
  deletedCount: number
}

export interface RestrictedZoneSyncRunSummary {
  runId: string
  startedAt: string
  finishedAt: string | null
  success: boolean
  zonesUpserted: number
  zonesMarkedDeleted: number
  errorsJson: string | null
}

export interface RestrictedZoneSyncSourceStatus {
  source: 'Ingemmet' | 'Sernanp' | 'CulturaSigda'
  lastRun: RestrictedZoneSyncRunSummary | null
  layers: RestrictedZoneSyncLayer[]
}

export interface RestrictedZoneSyncStatus {
  bySource: RestrictedZoneSyncSourceStatus[]
}

export interface RestrictedZoneDetail {
  id: string
  source: 'Ingemmet' | 'Sernanp' | 'CulturaSigda'
  layer: string
  externalId: string
  name: string
  category: string | null
  norma: string | null
  entidad: string | null
  fechaPublicacion: string | null
  restrictionClass: keyof typeof RestrictionClass
  isReferential: boolean
  syncedAt: string
  sourceUrl: string | null
  attributesJson: string | null
}

const RESTRICTION_CLASS_FROM_NAME: Record<string, RestrictionClassValue> = {
  Referential: RestrictionClass.Referential,
  HighSensitivity: RestrictionClass.HighSensitivity,
  SectoralValidationRequired: RestrictionClass.SectoralValidationRequired,
  OperationalMining: RestrictionClass.OperationalMining,
  HardStop: RestrictionClass.HardStop,
}

export const restrictionClassFromName = (
  name: keyof typeof RestrictionClass,
): RestrictionClassValue => RESTRICTION_CLASS_FROM_NAME[name] ?? RestrictionClass.Referential

const SOURCE_FROM_NAME: Record<string, 1 | 2 | 3> = {
  Ingemmet: 1,
  Sernanp: 2,
  CulturaSigda: 3,
}
export const sourceFromName = (
  name: 'Ingemmet' | 'Sernanp' | 'CulturaSigda',
): 1 | 2 | 3 => SOURCE_FROM_NAME[name]

export const intelSourceLabel = (name: 'Ingemmet' | 'Sernanp' | 'CulturaSigda'): string =>
  sourceLabel(sourceFromName(name))

export const intelRestrictionClassLabel = (name: keyof typeof RestrictionClass): string =>
  restrictionClassLabel(restrictionClassFromName(name))

export const intelRestrictionClassColor = (name: keyof typeof RestrictionClass): string =>
  restrictionClassColor(restrictionClassFromName(name))
